import React, { useEffect, useMemo, useRef, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Skeleton } from "primereact/skeleton";
import Swal from "sweetalert2";
import { getAuthUser } from "../../services/auth";
import "./Vendor.css";
import { Button } from "primereact/button";

export default function VendorTable({
  vendors,
  onEdit,
  onDelete,
  tableLoading,
  page,
  perPageSize,
  search,
}) {
  const auth = getAuthUser();
  // console.log("search : ", search);
  // console.log(vendors);

  /* =======================
     FETCH STATE (BIAR GA MUNCUL "TIDAK ADA DATA" DI AWAL)
     ======================= */
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false);
  const startedFetchRef = useRef(false);

  useEffect(() => {
    // kalau loading mulai, tandain fetch sudah pernah dimulai
    if (tableLoading) startedFetchRef.current = true;

    // kalau loading selesai dan fetch pernah dimulai -> berarti sudah ada hasil fetch minimal 1x
    if (!tableLoading && startedFetchRef.current) {
      setHasFetchedOnce(true);
    }
  }, [tableLoading]);

  // sebelum fetch pertama selesai, paksa skeleton biar ga muncul emptyMessage
  const showSkeleton = tableLoading || !hasFetchedOnce;

  /* =======================
     SKELETON SETUP
     ======================= */
  const skeletonRows = useMemo(
    () =>
      Array.from({ length: perPageSize }, (_, i) => ({
        id: `skeleton-${i}`,
      })),
    [perPageSize]
  );

  const skeletonBody = () => <Skeleton width="100%" height="1.4rem" />;

  /* =======================
     HIGHLIGHT LOGIC
     ======================= */
  const escapeRegex = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  const highlightText = (text, keyword) => {
    if (!keyword) return text || "-";

    keyword = keyword.replace(/[^\p{L}\p{N}]+/gu, " ");
    const keywords = Array.from(
      new Set(keyword.trim().split(/\s+/).filter(Boolean))
    ).map(escapeRegex);

    if (keywords.length === 0) return text || "-";

    const regex = new RegExp(`(${keywords.join("|")})`, "gi");

    return (
      <span
        dangerouslySetInnerHTML={{
          __html: String(text || "-").replace(
            regex,
            `<span class="highlight-vendor-table">$1</span>`
          ),
        }}
      />
    );
  };

  /* =======================
     COLUMN BODY
     ======================= */
  const numberBody = (_, options) =>
    showSkeleton
      ? skeletonBody()
      : options.rowIndex + 1 + (page - 1) * perPageSize;

  const phoneBody = (row) => {
    if (showSkeleton) return skeletonBody();

    const encodedPhone = row.phone;
    const countryCode = row.country_code;

    if (!encodedPhone || encodedPhone.trim() === "") {
      return <span className="text-muted">-</span>;
    }

    const handleClick = (e) => {
      e.preventDefault();

      let decodedPhone = "";
      try {
        decodedPhone = atob(encodedPhone);
      } catch {
        alert("Nomor tidak valid");
        return;
      }

      const cleanPhone = decodedPhone.replace(/\D/g, "").replace(/^0+/, "");
      const fullPhone = `${countryCode}${cleanPhone}`;

      if (countryCode) {
        const waLink = `https://wa.me/${
          fullPhone.startsWith("0") ? "62" + fullPhone.slice(1) : fullPhone
        }`;
        window.open(waLink, "_blank", "noopener,noreferrer");
      } else {
        navigator.clipboard.writeText(decodedPhone);
        Swal.fire({
          title: `Nomor telpon rumah disalin: ${decodedPhone}`,
          icon: "success",
          draggable: true,
        });
      }
    };

    return (
      <button
        onClick={handleClick}
        className="btn btn-link text-success d-flex align-items-center justify-content-center gap-1 text-decoration-none"
      >
        {countryCode ? (
          <>
            <i className="fa-brands fa-whatsapp fs-3"></i>
            <span>Whatsapp</span>
          </>
        ) : (
          <>
            <i className="fa-solid fa-phone fs-3"></i>
            <span>Telp Rumah</span>
          </>
        )}
      </button>
    );
  };

  const productTypeBody = (row) => {
    if (showSkeleton) return skeletonBody();

    const value = row.product_type || "-";
    const items = value
      .split("+")
      .map((v) => v.trim())
      .filter(Boolean);

    const colors = [
      "#0046b0",
      "#9e7700",
      "#a61f2d",
      "#156b43",
      "#0994b0",
      "#43494f",
    ];

    return (
      <div className="d-flex flex-wrap gap-2">
        {items.length > 0 ? (
          items.map((item, idx) => (
            <span
              key={idx}
              className="badge text-white"
              style={{ backgroundColor: colors[idx % colors.length] }}
            >
              {highlightText(item, search)}
            </span>
          ))
        ) : (
          <span>-</span>
        )}
      </div>
    );
  };

  const actionBody = (row) =>
    showSkeleton ? (
      skeletonBody()
    ) : (
      <React.Fragment>
        <Button
          icon="pi pi-pencil"
          className="action-btn edit-btn mr-2 rounded"
          rounded
          outlined
          onClick={() => onEdit(row)}
        />
        <Button
          icon="pi pi-trash"
          className="action-btn delete-btn rounded"
          rounded
          outlined
          onClick={() => onDelete(row.id)}
        />
      </React.Fragment>
    );

  const shouldShowEmpty =
    hasFetchedOnce && !tableLoading && vendors.length === 0;

  /* =======================
     RENDER
     ======================= */
  return (
    <DataTable
      value={showSkeleton ? skeletonRows : vendors}
      loading={false}
      scrollable
      scrollHeight="543px"
      showGridlines
      frozenHeader
      responsiveLayout="scroll"
      rowHover
      emptyMessage={shouldShowEmpty ? "Tidak ada data" : null}
      className="p-datatable-sm vendor-datatable"
    >
      <Column
        header="No"
        body={numberBody}
        style={{ width: "60px", textAlign: "center", cursor: "pointer" }}
      />

      <Column
        header="Nama Perusahaan"
        body={(row) =>
          showSkeleton
            ? skeletonBody()
            : highlightText(row.company_name, search)
        }
        style={{ minWidth: "200px", cursor: "pointer" }}
      />

      <Column
        header="Cabang"
        body={(row) =>
          showSkeleton ? skeletonBody() : highlightText(row.branch, search)
        }
        style={{ minWidth: "200px", cursor: "pointer" }}
      />

      <Column
        header="Merk"
        body={(row) =>
          showSkeleton ? skeletonBody() : highlightText(row.brand, search)
        }
        style={{ minWidth: "200px", cursor: "pointer" }}
      />

      <Column
        header="Nama Sales"
        body={(row) =>
          showSkeleton ? skeletonBody() : highlightText(row.sales_name, search)
        }
        style={{ minWidth: "200px", cursor: "pointer" }}
      />

      <Column
        header="Jabatan"
        body={(row) =>
          showSkeleton ? skeletonBody() : highlightText(row.position, search)
        }
        style={{ minWidth: "200px", cursor: "pointer" }}
      />

      <Column
        header="No HP / Telp Kantor"
        body={phoneBody}
        style={{ minWidth: "120px", textAlign: "center", cursor: "pointer" }}
      />

      <Column
        header="Email"
        body={(row) =>
          showSkeleton ? skeletonBody() : highlightText(row.email, search)
        }
        style={{ minWidth: "200px", cursor: "pointer" }}
      />

      <Column
        header="Jenis Barang"
        body={productTypeBody}
        style={{ minWidth: "250px", cursor: "pointer" }}
      />

      {auth?.role === "admin" && (
        <Column
          header="Aksi"
          body={actionBody}
          frozen
          alignFrozen="right"
          style={{ minWidth: "150px", textAlign: "center", cursor: "pointer" }}
          headerStyle={{ textAlign: "center" }}
        />
      )}
    </DataTable>
  );
}
